/**
 * Поле «Иконка», выбор значка и строка цвета — одни для счёта и категории
 * (макет 14.10.2026, экраны 3–4).
 *
 * Поле оформлено как поле ввода: слева подпись, справа выбранный значок в его
 * цвете и стрелка. Название значка («Покупки», «Яблоко») не пишется — значок
 * и так виден, а слово рядом читалось как ещё одно поле.
 *
 * Выбор — шагом внутри того же листа: поиск и группы. У счёта сверху логотипы
 * банков и значки денег, дальше весь набор категорий.
 */
import { useState } from 'react';
import { ACCOUNT_ICON_CHOICES, ICON_GROUPS, PACK_ICONS, type PackIcon } from './icon-pack.js';
import { BANKS, bankIcon, type Bank } from './banks.js';
import { IconGlyph } from './account-glyph.js';
import { CATEGORY_COLORS, COLOR_LABEL, type CategoryColor } from './category-icon.js';
import { categoryKey } from './category-tree.js';
import { CloseX } from './close-icon.js';
import { Icon } from './icon.js';

export function IconField({ icon, color, onOpen }: { icon: string; color: CategoryColor; onOpen: () => void }) {
  return (
    <button type="button" className="iconfield" onClick={onOpen} aria-label="Иконка. Сменить">
      <span className="lbl">Иконка</span>
      <span className="r"><IconGlyph icon={icon} color={color} /><Icon name="chevron-right" size={13} /></span>
    </button>
  );
}

/** Семь цветов, те же у счёта и категории. */
export function ColorRow({ value, onChange }: { value: CategoryColor; onChange: (c: CategoryColor) => void }) {
  return (
    <>
      <div className="fieldhead">Цвет</div>
      <div className="colorrow">{CATEGORY_COLORS.map((c) => (
        <button type="button" key={c} className={`swatch c-${c}${value === c ? ' on' : ''}`} aria-label={`Цвет: ${COLOR_LABEL[c]}`}
          aria-pressed={value === c} onClick={() => onChange(c)} />
      ))}</div>
    </>
  );
}

const ACCOUNT_PACK: PackIcon[] = ACCOUNT_ICON_CHOICES.map((c) => ({ name: c.name, label: c.label, keywords: '' }));

export function IconPicker({ value, color, forAccount = false, onPick }: {
  value: string;
  color: CategoryColor;
  /** Выбор для счёта: сверху банки и значки денег. */
  forAccount?: boolean;
  onPick: (icon: string) => void;
}) {
  const [search, setSearch] = useState('');
  const q = categoryKey(search);
  const fits = (i: PackIcon) => !q || categoryKey(i.label).includes(q) || categoryKey(i.keywords).includes(q) || i.name.includes(q);
  const bankFits = (b: Bank) => !q || categoryKey(b.name).includes(q) || b.id.includes(q) || categoryKey('банк').includes(q);
  const cell = (name: string, label: string, glyphColor: CategoryColor) => (
    <button type="button" key={name} className={`iconpick${value === name ? ' on' : ''}`} aria-label={`Иконка: ${label}`}
      aria-pressed={value === name} onClick={() => onPick(name)}>
      <IconGlyph icon={name} color={glyphColor} />
    </button>
  );
  const sections: { title: string; cells: JSX.Element[] }[] = [];
  if (forAccount) {
    sections.push({ title: 'Банки', cells: BANKS.filter(bankFits).map((b) => cell(bankIcon(b.id), b.name, color)) });
    sections.push({ title: 'Счета', cells: ACCOUNT_PACK.filter(fits).map((i) => cell(i.name, i.label, value === i.name ? color : 'gray')) });
  }
  // Значки денег у счёта уже показаны выше — второй раз в общем наборе не нужны.
  const shown = new Set(forAccount ? ACCOUNT_PACK.map((i) => i.name) : []);
  ICON_GROUPS.forEach((g, gi) => {
    // Выбранный значок категории — первым: список открывается с того, что
    // уже выбрано, и не заставляет искать его глазами.
    const chosen = !forAccount && gi === 0 ? PACK_ICONS.find((i) => i.name === value) : undefined;
    const icons = [...(chosen && fits(chosen) ? [chosen] : []),
      ...g.icons.filter((i) => fits(i) && !shown.has(i.name) && i.name !== chosen?.name)];
    icons.forEach((i) => shown.add(i.name));
    sections.push({ title: g.title, cells: icons.map((i) => cell(i.name, i.label, value === i.name ? color : 'gray')) });
  });
  const visible = sections.filter((s) => s.cells.length > 0);
  return (
    <div className="iconpicker">
      <div className="search-field quiet">
        <Icon name="magnifying-glass" size={17} />
        <input type="search" aria-label="Поиск иконок" placeholder={forAccount ? 'Банк или значок' : 'Поиск иконок'}
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button type="button" aria-label="Очистить поиск иконок" onClick={() => setSearch('')}><CloseX /></button>}
      </div>
      {visible.map((s) => (
        <section className="iconpack" key={s.title}>
          <div className="iconpack-title">{s.title}</div>
          <div className="iconpack-grid">{s.cells}</div>
        </section>
      ))}
      {visible.length === 0 && <p className="note">Такой иконки нет — попробуй другое слово.</p>}
    </div>
  );
}
